import { useState, useEffect } from 'react';
import { Minus, Square, X, Copy, Bell, BellOff } from 'lucide-react';
import { isLocalShell } from '../lib/platform';
import { useBackend } from '@/services/backend';
import UserIndicator from './UserIndicator';
import ThemeSelector from './ThemeSelector';
import { useUIStore } from '@/stores/uiStore';

function DndToggle() {
  const dndEnabled = useUIStore((s) => s.dndEnabled);
  const setDnd = useUIStore((s) => s.setDnd);

  return (
    <button
      onClick={() => setDnd(!dndEnabled)}
      className={`h-6 w-6 flex items-center justify-center rounded transition-colors ${
        dndEnabled
          ? 'text-amber-400 bg-amber-400/10 hover:bg-amber-400/20'
          : 'text-base-content/40 hover:text-base-content/70 hover:bg-base-300'
      }`}
      title={dndEnabled ? 'Notifications silenced (click to enable)' : 'Silence notifications'}
    >
      {dndEnabled ? (
        <BellOff className="w-3.5 h-3.5" />
      ) : (
        <Bell className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

export default function TitleBar() {
  const [isMaximized, setIsMaximized] = useState(false);
  const backend = useBackend();
  // The custom titlebar renders under BOTH local-desktop shells (Tauri & CEF),
  // which run a frameless window and expose `backend.window` for the controls.
  const win = backend.window;

  useEffect(() => {
    if (!isLocalShell() || !win) return;

    let cancelled = false;

    const checkMaximized = async () => {
      try {
        const maximized = await win.isMaximized();
        if (!cancelled) setIsMaximized(maximized);
      } catch {
        // Ignore errors during initialization
      }
    };

    checkMaximized();

    // Re-check on resize
    const onResize = () => checkMaximized();
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
    };
  }, [win]);

  if (!isLocalShell() || !win) return null;

  const handleMinimize = async () => {
    await win.minimize();
  };

  const handleMaximizeRestore = async () => {
    const maximized = await win.toggleMaximize();
    setIsMaximized(maximized);
  };

  const handleClose = async () => {
    await win.close();
  };

  // Drag region: under the CEF host (where `data-tauri-drag-region` is inert) we
  // start a native move-drag on mouse-down via the host bridge. Only fire for the
  // primary button, and never when the press lands on an interactive control
  // (button), so the window controls + theme toggle stay clickable.
  const handleDragMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return;
    // Don't start the window-move drag on mousedown — that begins a GTK pointer
    // grab that eats the SECOND click of a double-click, so dbl-click-to-maximize
    // never fires. Only begin dragging once the pointer actually moves past a
    // small threshold; a plain click / double-click (no movement) is left alone.
    const sx = e.clientX, sy = e.clientY;
    const onMove = (ev: MouseEvent) => {
      if (Math.abs(ev.clientX - sx) > 4 || Math.abs(ev.clientY - sy) > 4) {
        cleanup();
        void win?.startDrag();
      }
    };
    const cleanup = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', cleanup);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', cleanup);
  };

  // Double-clicking the empty titlebar drag region maximizes/restores the
  // window — the standard desktop affordance. Ignore double-clicks that land
  // on a window control (button) so e.g. dbl-clicking Minimize doesn't also
  // toggle maximize. Works in both the CEF and Tauri local shells via the
  // backend.window bridge.
  const handleTitleDoubleClick = async (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const maximized = await win.toggleMaximize();
    setIsMaximized(maximized);
  };

  return (
    <div
      data-tauri-drag-region
      onMouseDown={handleDragMouseDown}
      onDoubleClick={handleTitleDoubleClick}
      className="h-9 flex items-center justify-between bg-base-200 border-b border-base-300 select-none flex-shrink-0"
    >
      {/* Left: App name */}
      <div data-tauri-drag-region onMouseDown={handleDragMouseDown} onDoubleClick={handleTitleDoubleClick} className="flex items-center pl-3">
        <span className="text-sm font-semibold text-base-content/80">AtelierCode</span>
      </div>

      {/* Center-right: DND + User indicator + theme */}
      <div className="flex items-center gap-1 mr-1">
        <DndToggle />
        <UserIndicator />
        <ThemeSelector />
      </div>

      {/* Right: Window controls */}
      <div className="flex items-center h-full">
        <button
          onClick={handleMinimize}
          className="h-full w-11 flex items-center justify-center hover:bg-base-300 transition-colors"
          title="Minimize"
        >
          <Minus className="w-4 h-4" />
        </button>
        <button
          onClick={handleMaximizeRestore}
          className="h-full w-11 flex items-center justify-center hover:bg-base-300 transition-colors"
          title={isMaximized ? 'Restore' : 'Maximize'}
        >
          {isMaximized ? (
            <Copy className="w-3.5 h-3.5" />
          ) : (
            <Square className="w-3.5 h-3.5" />
          )}
        </button>
        <button
          onClick={handleClose}
          className="h-full w-11 flex items-center justify-center hover:bg-error hover:text-error-content transition-colors"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
